import Link from '@/lib/link'
import { ContentPage, Prose, Section } from '@/components/content-page'

export default function DocsPage() {
  return (
    <ContentPage
      eyebrow="// guide"
      title="Get comfortable with NewEmail"
      description="Set up an account, understand the first sync, and keep your mail fast and secure."
      wide
    >
      <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-3">
        {[
          ['1', 'Add an account', 'Use your existing address and let automatic discovery find the server settings.'],
          ['2', 'Wait for the first sync', 'Recent folders and messages are copied into the local cache.'],
          ['3', 'Make it yours', 'Add a signature, choose your folders, and settle into the workspace.'],
        ].map(([number, title, body]) => (
          <div key={number} className="rounded-2xl border border-border bg-card p-6">
            <span className="font-mono text-sm font-bold text-primary">{number.padStart(2, '0')}</span>
            <h2 className="mt-3 text-lg font-bold">{title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
          </div>
        ))}
      </div>

      <div className="mx-auto max-w-4xl pt-10">
        <Prose>
          <Section title="Common provider settings">
            <p>
              NewEmail tries to discover settings from your address. If your provider does not
              publish them automatically, use the provider’s IMAP and SMTP documentation.
            </p>
            <div className="overflow-x-auto rounded-xl border border-border bg-card">
              <table className="w-full min-w-[36rem] text-left text-sm">
                <thead className="border-b border-border bg-secondary/60 text-foreground">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Provider</th>
                    <th className="px-4 py-3 font-semibold">Common guide</th>
                    <th className="px-4 py-3 font-semibold">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['iCloud', 'imap.mail.me.com:993 · smtp.mail.me.com:587', 'Use an app-specific password.'],
                    ['Gmail', 'imap.gmail.com:993 · smtp.gmail.com:587', 'Use an app password if required.'],
                    ['Outlook', 'outlook.office365.com:993 · smtp.office365.com:587', 'Check Microsoft account security settings.'],
                    ['Fastmail', 'imap.fastmail.com:993 · smtp.fastmail.com:465', 'SSL/TLS for both servers.'],
                  ].map(([provider, servers, note]) => (
                    <tr key={provider} className="border-b border-border last:border-0">
                      <td className="px-4 py-3 font-semibold text-foreground">{provider}</td>
                      <td className="px-4 py-3 font-mono text-xs">{servers}</td>
                      <td className="px-4 py-3">{note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="If your account will not connect">
            <p>
              Confirm the address, password, server name, port, and security mode. Some providers
              require an app-specific password or enable IMAP separately. Corporate accounts may
              also require an administrator to permit a mail client.
            </p>
            <p>
              Before changing advanced settings, copy the server details exactly from your provider
              or compare them with a working mail client.
            </p>
          </Section>

          <Section title="Mail storage and privacy">
            <p>
              NewEmail keeps a local cache so folders open quickly and old messages remain
              searchable. Account credentials are stored separately in your device’s protected
              keychain. Your provider still holds the authoritative mail data.
            </p>
          </Section>

          <Section title="Need more help?">
            <div className="flex flex-wrap gap-3">
              <Link
                href="/faq"
                className="rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/40"
              >
                Read the FAQ
              </Link>
              <Link
                href="/forum"
                className="rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/40"
              >
                Ask the community
              </Link>
              <Link
                href="/support"
                className="rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/40"
              >
                Report a problem
              </Link>
            </div>
          </Section>
        </Prose>
      </div>
    </ContentPage>
  )
}
